import React, { useEffect, useRef, useState } from "react";
import { db } from "../firebase";
import { doc, setDoc } from "firebase/firestore";

const SESSION_ID = "grey";
const BROKEN_THRESHOLD = 20;

const NO_LABELS = [
  "no",
  "no",
  "nope",
  "still no",
  "lol no",
  "try again",
  "you sure?",
  "really?",
  "are you sure",
  "think harder",
  "not that one",
  "wrong button",
  "almost",
  "warmer",
  "colder",
  "give up?",
  "👀",
  "🥺",
  "...",
  "ok last try",
];

const DODGE_MODES = [
  "teleport",
  "edge",
  "shrink",
  "spin",
  "tiny",
  "wobble",
  "flip",
  "slide",
];

const SAFE_GAP = 24;

export default function DateAsk({ goTo }) {
  const [answered, setAnswered] = useState(false);
  const [dodges, setDodges] = useState(0);
  const [noStyle, setNoStyle] = useState(null);
  const [confetti, setConfetti] = useState([]);
  const [broken, setBroken] = useState(false);
  const [glitchOn, setGlitchOn] = useState(false);
  const wrapRef = useRef(null);
  const noBtnRef = useRef(null);
  const yesBtnRef = useRef(null);
  const audioCtxRef = useRef(null);

  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };

  const beep = (freq, duration = 0.12, type = "square", gain = 0.08) => {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  };

  const playDodgeTick = (n) => {
    const base = 220 + Math.min(n, 18) * 14;
    beep(base, 0.06, "square", 0.04);
  };

  const playBrokenSfx = () => {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    [880, 660, 440, 330, 220].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(f, now + i * 0.12);
      g.gain.setValueAtTime(0.08, now + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.18);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.2);
    });
    const buzzOsc = ctx.createOscillator();
    const buzzGain = ctx.createGain();
    buzzOsc.type = "square";
    buzzOsc.frequency.setValueAtTime(110, now + 0.7);
    buzzOsc.frequency.linearRampToValueAtTime(60, now + 1.4);
    buzzGain.gain.setValueAtTime(0.07, now + 0.7);
    buzzGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.4);
    buzzOsc.connect(buzzGain);
    buzzGain.connect(ctx.destination);
    buzzOsc.start(now + 0.7);
    buzzOsc.stop(now + 1.5);
  };

  const playYesSfx = () => {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      g.gain.setValueAtTime(0, now + i * 0.06);
      g.gain.linearRampToValueAtTime(0.08, now + i * 0.06 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.06 + 0.6);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(now + i * 0.06);
      osc.stop(now + i * 0.06 + 0.7);
    });
  };

  const pickMode = (n) => {
    if (n < 3) return "teleport";
    if (n < 6) return DODGE_MODES[Math.floor(Math.random() * 4)];
    return DODGE_MODES[Math.floor(Math.random() * DODGE_MODES.length)];
  };

  const overlapsYes = (x, y, w, h) => {
    if (!wrapRef.current || !yesBtnRef.current) return false;
    const wrap = wrapRef.current.getBoundingClientRect();
    const yes = yesBtnRef.current.getBoundingClientRect();
    const yesX = yes.left - wrap.left - SAFE_GAP;
    const yesY = yes.top - wrap.top - SAFE_GAP;
    const yesW = yes.width + SAFE_GAP * 2;
    const yesH = yes.height + SAFE_GAP * 2;
    return !(
      x + w < yesX ||
      x > yesX + yesW ||
      y + h < yesY ||
      y > yesY + yesH
    );
  };

  const findSafeSpot = (btnW, btnH, maxX, maxY, attempts = 30) => {
    for (let i = 0; i < attempts; i++) {
      const x = Math.random() * maxX;
      const y = Math.random() * maxY;
      if (!overlapsYes(x, y, btnW, btnH)) return { x, y };
    }
    return { x: 8, y: 8 };
  };

  const handleNoHover = () => {
    if (broken) return;
    if (!wrapRef.current || !noBtnRef.current) return;
    const wrap = wrapRef.current.getBoundingClientRect();
    const btn = noBtnRef.current.getBoundingClientRect();
    const maxX = wrap.width - btn.width - 16;
    const maxY = wrap.height - btn.height - 16;
    const next = dodges + 1;
    const mode = pickMode(dodges);

    let style = { position: "absolute", transition: "all 0.22s ease" };
    const placeSafe = () => {
      const { x, y } = findSafeSpot(btn.width, btn.height, maxX, maxY);
      style.left = `${x}px`;
      style.top = `${y}px`;
    };

    switch (mode) {
      case "edge": {
        const corners = [
          { x: 0, y: 0 },
          { x: maxX, y: 0 },
          { x: 0, y: maxY },
          { x: maxX, y: maxY },
          { x: maxX / 2, y: 0 },
          { x: maxX / 2, y: maxY },
        ];
        const safe = corners.filter(
          (c) => !overlapsYes(c.x, c.y, btn.width, btn.height)
        );
        const c = (safe.length ? safe : corners)[
          Math.floor(Math.random() * (safe.length || corners.length))
        ];
        style.left = `${c.x}px`;
        style.top = `${c.y}px`;
        style.transform = `skewX(-12deg) rotate(${
          (Math.random() - 0.5) * 30
        }deg)`;
        break;
      }
      case "shrink":
        placeSafe();
        style.transform = `skewX(-12deg) scale(${0.5 + Math.random() * 0.3})`;
        break;
      case "spin":
        placeSafe();
        style.transform = `skewX(-12deg) rotate(${
          Math.random() * 720 - 360
        }deg)`;
        style.transition = "all 0.5s cubic-bezier(0.22, 1, 0.36, 1)";
        break;
      case "tiny":
        placeSafe();
        style.transform = `skewX(-12deg) scale(${0.35 + Math.random() * 0.2})`;
        break;
      case "wobble":
        placeSafe();
        style.transform = `skewX(-12deg) translate(${
          (Math.random() - 0.5) * 12
        }px, ${(Math.random() - 0.5) * 12}px) rotate(${
          (Math.random() - 0.5) * 14
        }deg)`;
        style.transition = "all 0.08s ease";
        break;
      case "flip":
        placeSafe();
        style.transform = `skewX(-12deg) rotate(${
          Math.random() > 0.5 ? 180 : -180
        }deg) scale(${0.7 + Math.random() * 0.3})`;
        style.transition = "all 0.4s ease";
        break;
      case "slide": {
        const targets = [
          { x: 0, y: Math.random() * maxY },
          { x: maxX, y: Math.random() * maxY },
          { x: Math.random() * maxX, y: 0 },
          { x: Math.random() * maxX, y: maxY },
        ];
        const safe = targets.filter(
          (t) => !overlapsYes(t.x, t.y, btn.width, btn.height)
        );
        const tg = (safe.length ? safe : targets)[
          Math.floor(Math.random() * (safe.length || targets.length))
        ];
        style.left = `${tg.x}px`;
        style.top = `${tg.y}px`;
        style.transform = `skewX(-12deg)`;
        style.transition = "all 0.35s cubic-bezier(0.5, 0, 0.2, 1)";
        break;
      }
      case "teleport":
      default:
        placeSafe();
        style.transform = `skewX(-12deg) rotate(${
          (Math.random() - 0.5) * 20
        }deg)`;
        break;
    }

    setNoStyle(style);
    setDodges(next);
    playDodgeTick(next);

    if (next === BROKEN_THRESHOLD) {
      setTimeout(() => {
        setBroken(true);
        setGlitchOn(true);
        playBrokenSfx();
        setTimeout(() => setGlitchOn(false), 1500);
      }, 250);
    }
  };

  const launchConfetti = () => {
    const pieces = Array.from({ length: 60 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.4,
      duration: 1.6 + Math.random() * 1.4,
      color: ["#f99e1a", "#40b3ff", "#ff4d6d", "#c9a0ff", "#ffffff"][
        Math.floor(Math.random() * 5)
      ],
      rotate: Math.random() * 360,
      size: 6 + Math.random() * 8,
    }));
    setConfetti(pieces);
  };

  const handleYes = async () => {
    setAnswered(true);
    launchConfetti();
    playYesSfx();
    try {
      const ref = doc(db, "sessions", SESSION_ID);
      await setDoc(
        ref,
        { dateAsk: { answer: "yes", at: Date.now() } },
        { merge: true }
      );
    } catch (e) {}
  };

  const handleNoClick = (e) => {
    e.preventDefault();
    if (broken) return;
    handleNoHover();
  };

  useEffect(() => {
    if (!answered) return;
    const t = setTimeout(() => {
      const el = document.getElementById("date-continue");
      if (el) el.style.opacity = "1";
    }, 900);
    return () => clearTimeout(t);
  }, [answered]);

  const noLabel = broken
    ? "ERR_404"
    : NO_LABELS[Math.min(dodges, NO_LABELS.length - 1)];

  const defaultNoStyle = {
    position: "absolute",
    left: "50%",
    top: "calc(50% + 70px)",
    transform: "translate(-50%, 0) skewX(-12deg)",
    transition: "all 0.22s ease",
  };

  return (
    <div className="scene dateask-scene">
      <h1 className="title">the real question</h1>

      <p className="subtitle dateask-copy">
        date? saturday. 8pm cst. re-animal game. me &amp; you.
      </p>

      {!answered ? (
        <div className="dateask-buttons" ref={wrapRef}>
          <button ref={yesBtnRef} className="btn btn-yes" onClick={handleYes}>
            <span className="btn-text">yes</span>
          </button>

          <button
            ref={noBtnRef}
            className={`btn btn-no ${broken ? "btn-no-broken" : ""} ${
              glitchOn ? "btn-no-glitching" : ""
            }`}
            style={noStyle ?? defaultNoStyle}
            onMouseEnter={handleNoHover}
            onFocus={handleNoHover}
            onClick={handleNoClick}
            disabled={broken}
          >
            <span className="btn-text">{noLabel}</span>
          </button>

          {broken && (
            <div className="broken-msg">
              <span>// no_button.exe has stopped responding</span>
            </div>
          )}
        </div>
      ) : (
        <div className="dateask-yes">
          <div className="yes-big">yes 🖤</div>
          <p className="subtitle">see you saturday, grey.</p>
          <button
            id="date-continue"
            className="btn"
            style={{ opacity: 0, transition: "opacity 0.6s ease" }}
            onClick={() => goTo("finale")}
          >
            <span className="btn-text">one more thing →</span>
          </button>
        </div>
      )}

      <div className="confetti-layer" aria-hidden="true">
        {confetti.map((c) => (
          <span
            key={c.id}
            className="confetti-piece"
            style={{
              left: `${c.left}%`,
              width: c.size,
              height: c.size,
              background: c.color,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`,
              transform: `rotate(${c.rotate}deg)`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
